import React, { useState, useEffect, useRef } from "react";
import {
  History,
  Clock,
  Plus,
  PanelLeftClose,
  Send,
  RefreshCw,
  Check,
  Loader,
  Trash2,
  Sparkles,
  Wand2,
  Zap,
  Undo,
  Redo,
  Save,
  Download,
} from "lucide-react";
import { ChatHistory, ChatMessage } from "./ChatHistory";
import { Checkpoint, ProgressTask, RecentPattern } from "../../types/pattern";

interface ChatPanelProps {
  chatHistory: ChatMessage[];
  onSendMessage: (message: string) => void;
  isGenerating: boolean;
  progressTasks: ProgressTask[];
  progress: number;
  progressMessage: string;
  onToggleCheckpoints: () => void;
  onToggleRecentPatterns: () => void;
  onToggleChatVisibility: () => void;
  showCheckpoints: boolean;
  showRecentPatterns: boolean;
  checkpoints: Checkpoint[];
  onRollbackToCheckpoint: (checkpointId: string) => void;
  recentPatterns: RecentPattern[];
}

export function ChatPanel({
  chatHistory,
  onSendMessage,
  isGenerating,
  progressTasks,
  progress,
  progressMessage,
  onToggleCheckpoints,
  onToggleRecentPatterns,
  onToggleChatVisibility,
  showCheckpoints,
  showRecentPatterns,
  checkpoints,
  onRollbackToCheckpoint,
  recentPatterns,
}: ChatPanelProps) {
  const [promptInput, setPromptInput] = useState("");

  const handleSubmit = () => {
    if (promptInput.trim()) {
      onSendMessage(promptInput.trim());
      setPromptInput("");
    }
  };

  return (
    <div className="w-full h-full flex flex-col bg-[#161b22]">
      {/* Chat header */}
      <div className="p-4 border-b border-[#30363d] flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <button
            onClick={onToggleCheckpoints}
            className={`p-2 rounded-lg transition-colors ${
              showCheckpoints
                ? "bg-[#1f6feb]/20 text-[#58a6ff]"
                : "text-gray-400 hover:text-[#58a6ff] hover:bg-[#1f6feb]/10"
            }`}
            title="View checkpoints"
          >
            <History className="h-4 w-4" />
          </button>
          <button
            onClick={onToggleRecentPatterns}
            className={`p-2 rounded-lg transition-colors ${
              showRecentPatterns
                ? "bg-[#1f6feb]/20 text-[#58a6ff]"
                : "text-gray-400 hover:text-[#58a6ff] hover:bg-[#1f6feb]/10"
            }`}
            title="Recent patterns"
          >
            <Clock className="h-4 w-4" />
          </button>
        </div>
        <button
          onClick={onToggleChatVisibility}
          className="p-2 rounded-lg text-gray-400 hover:text-[#58a6ff] hover:bg-[#1f6feb]/10 transition-colors"
          title="Hide chat"
        >
          <PanelLeftClose className="h-4 w-4" />
        </button>
      </div>

      {/* Chat content */}
      <div className="flex-1 overflow-y-auto p-4 bg-[#0d1117]">
        {showCheckpoints ? (
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-400 mb-3">
              Checkpoints
            </h3>
            {checkpoints.length > 0 ? (
              checkpoints.map((checkpoint) => (
                <button
                  key={checkpoint.id}
                  onClick={() => onRollbackToCheckpoint(checkpoint.id)}
                  className="w-full text-left p-3 bg-[#161b22] hover:bg-[#1f6feb]/20 rounded-lg text-white text-sm transition-colors"
                >
                  <div className="font-medium">{checkpoint.description}</div>
                  <div className="text-xs text-gray-400 mt-1">
                    {new Date(checkpoint.timestamp).toLocaleString()}
                  </div>
                </button>
              ))
            ) : (
              <div className="text-center text-gray-500 py-8">
                <p>No checkpoints yet</p>
                <p className="text-sm mt-1">
                  Generate a pattern to create checkpoints
                </p>
              </div>
            )}
          </div>
        ) : showRecentPatterns ? (
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-400 mb-3">
              Recent Patterns
            </h3>
            {recentPatterns.map((pattern) => (
              <div
                key={pattern.id}
                className="p-3 bg-[#161b22] hover:bg-[#1f6feb]/20 rounded-lg text-white text-sm transition-colors cursor-pointer"
              >
                <div className="font-medium">{pattern.title}</div>
                <div className="text-xs text-gray-400 mt-1 line-clamp-2">
                  {pattern.preview}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {pattern.date.toLocaleDateString()}
                </div>
              </div>
            ))}
            <button className="w-full mt-4 flex items-center justify-center gap-2 p-2 bg-[#1f6feb]/20 text-[#58a6ff] rounded-lg hover:bg-[#1f6feb]/30 transition-colors">
              <Plus className="h-4 w-4" />
              <span>New Pattern</span>
            </button>
          </div>
        ) : (
          <ChatHistory messages={chatHistory} />
        )}
      </div>

      {/* Chat input */}
      <div className="p-4 border-t border-[#30363d]">
        <div className="relative">
          <textarea
            value={promptInput}
            onChange={(e) => setPromptInput(e.target.value)}
            placeholder="What would you like to create?"
            className="w-full px-4 py-3 pr-12 bg-[#161b22] border border-[#30363d] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#1f6feb] resize-none min-h-[100px]"
            disabled={isGenerating}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
          <button
            onClick={handleSubmit}
            disabled={isGenerating || !promptInput.trim()}
            className="absolute bottom-3 right-3 p-2 text-white bg-[#238636] hover:bg-[#2ea043] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGenerating ? (
              <RefreshCw className="h-5 w-5 animate-spin" />
            ) : (
              <Send className="h-5 w-5" />
            )}
          </button>
        </div>

        {/* Progress indicator */}
        {isGenerating && (
          <div className="mt-4 bg-[#161b22]/50 rounded-lg p-4">
            <div className="space-y-3">
              {progressTasks.map((task) => (
                <div key={task.id} className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-5">
                    {task.status === "completed" ? (
                      <Check className="h-4 w-4 text-[#3fb950]" />
                    ) : task.status === "in-progress" ? (
                      <Loader className="h-4 w-4 text-[#58a6ff] animate-spin" />
                    ) : (
                      <div className="h-4 w-4 rounded-full border border-gray-600" />
                    )}
                  </div>
                  <span
                    className={`text-xs ${
                      task.status === "completed"
                        ? "text-[#3fb950]"
                        : task.status === "in-progress"
                        ? "text-[#58a6ff]"
                        : "text-gray-400"
                    }`}
                  >
                    {task.name}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-3">
              <div className="w-full bg-gray-700 rounded-full h-1.5 mb-2">
                <div
                  className="bg-[#1f6feb] h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-400">{progressMessage}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
